// @ts-check
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { get_schedules } from '~/api/zero-api';
import { useCachedApiLoader } from '~/components/Util/CachedApiLoader';
import { useZeroEventListener } from '~/components/ZeroContext';
import { useCurrentUser } from '~/hooks/reduxHooks';
import useOrganizationId from '~/hooks/useOrganizationId';
import useZeroSelector from '~/hooks/useZeroSelector';
import { renameParamKey } from '~/other/Helper';
import NotificationAlert from '~/other/NotificationAlert';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

const PAGE_SIZE = 50;

export const EventNames = {
    REFRESH_DATA: "SchedulesCalendar:refreshData",
};

function useRefCountedState(initialValue) {
    const [value, setValue] = useState(initialValue);
    const [count, setCount] = useState(0);

    const incrementRefAndSetValue = useCallback((newValue) => {
        setCount((count) => count + 1);
        setValue(newValue);
    }, []);

    return useMemo(() => [value, count, incrementRefAndSetValue], [value, count]);
}

/**
 * @param {{
 *  pageType: FormsPageType,
 * }} props
 */
export default function ScheduleCalendar({ pageType }) {
    const orgId = useOrganizationId();
    const user = useCurrentUser();
    const formQuery = useZeroSelector((state) => state.forms.query);
    const history = useHistory();
    const cachedApiLoader = useCachedApiLoader();

    const [loadingSchedules, setLoadingSchedules] = useState(true);
    const [schedules, schedulesRefCount, setSchedules] = useRefCountedState([]);
    const [pagination] = useState({
        currentPage: 1,
        pageSize: PAGE_SIZE,
        totalSchedules: 0,
    });
    const calendarDataCache = useRef(null);

    const fetchSchedules = async (bypassCache = false) => {
        try {
            setLoadingSchedules(true);
            const params = new URLSearchParams(formQuery);
            params.set("form_types", pageType);
            params.set("page", pagination.currentPage.toFixed(0));
            params.set("per_page", pagination.pageSize.toFixed(0));
            renameParamKey(params, "schedule_status", "status");
            renameParamKey(params, "category_uuid", "category_uuids");

            const teamIdParam = params.get("team_uuid");

            if (teamIdParam === "my_teams" || teamIdParam === "organization_teams") {
                params.delete("team_uuid");
            }

            const content = await cachedApiLoader.fetch(get_schedules, `?${params}`, { bypassCache });
            const schedules = content.schedulers ?? [];
            setSchedules(schedules);
        } catch (err) {
            NotificationAlert("error", "", "Unable to load schedules.");
        } finally {
            setLoadingSchedules(false);
        }
    };

    const fetchSchedulesWithoutCache = () => fetchSchedules(true);

    useZeroEventListener(EventNames.REFRESH_DATA, fetchSchedulesWithoutCache);

    useEffect(() => {
        fetchSchedules();
    }, [formQuery, pagination.currentPage]);

    /**
     * @param {Date} startDate
     * @param {Date} endDate
     */
    const getRangeData = (startDate, endDate) => {
        const cacheKey = [startDate.toISOString(), endDate.toISOString(), schedulesRefCount].toString();

        if (calendarDataCache.current?.key === cacheKey) {
            return calendarDataCache.current?.data;
        }

        let allItems = [];
        const myToday = new Date(startDate);

        const endTime = new Date(endDate).getTime();
        let startTime = new Date(myToday).getTime();

        while (startTime <= endTime) {
            allItems.push(getListData(myToday, schedules));
            myToday.setDate(myToday.getDate() + 1);
            startTime = new Date(myToday).getTime();
        }

        const flattenedItems = allItems.flat();

        calendarDataCache.current = {
            key: cacheKey,
            data: flattenedItems,
        };
        return flattenedItems;
    };

    const fullCalendarEventRender = async (calInfo) => {
        const calStart = new Date(calInfo.start) || new Date();
        const calEnd = new Date(calInfo.end) || new Date();
        return getRangeData(calStart, calEnd);
    };

    const renderEventContent = (eventInfo) => {
        return (
            <div className={"zero-event"}>
                <b>{eventInfo.timeText}</b>
                {eventInfo.event.title}
            </div>
        );
    };

    if (loadingSchedules) {
        return <div>Loading...</div>;
    }

    return (
        <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            timeZone={user.timezone ?? ""}
            headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            initialView="dayGridMonth"
            weekends={true}
            events={fullCalendarEventRender}
            eventContent={renderEventContent}
            dayMaxEvents={true}
            dayMaxEventRows={3}
            eventMaxStack={3}
            eventClick={(info) => {
                history.push(
                    `/${orgId}/home/manage_schedules/analytics/${info.event.extendedProps.scheduler_uuid}`
                );
            }}
        />
    );
}

function changeTimezone(utcDate, localTz) {
    // suppose the date is 12:00 UTC
    const localDate = new Date(
        utcDate.toLocaleString("en-US", {
            timeZone: localTz,
        })
    );

    // then invdate will be 07:00 in Toronto
    // and the diff is 5 hours
    const diff = utcDate.getTime() - localDate.getTime();

    // so 12:00 in Toronto is 17:00 UTC
    return new Date(utcDate.getTime() + diff); // needs to adjust
}

/**
 * @param {Date} calendarDate
 * @param {any[]} schedules
 */
function getListData(calendarDate, schedules) {
    let activeSchedules = schedules.filter((item) => item.enabled);
    let filteredCalendar = [];

    if (activeSchedules.length > 0) {
        for (const schedule of activeSchedules) {
            const startDate = new Date(schedule.start_date);
            const endDate = schedule.end_date ? new Date(schedule.end_date) : null;
            if (
                calendarDate.getTime() >= startDate.getTime() &&
                (endDate == null || calendarDate.getTime() <= endDate.getTime())
            ) {
                // schedule.repeat_period === 0 ? "Daily" :
                let newStart = new Date(calendarDate);
                let [hours, minutes, seconds] = [9, 0, 0];
                let customTimezone = "America/New_York";
                if (schedule.custom_start_time) {
                    [hours, minutes, seconds] = schedule.custom_start_time.split(":");
                }
                if (schedule.custom_start_timezone) {
                    customTimezone = schedule.custom_start_timezone;
                }
                newStart.setUTCHours(hours);
                newStart.setUTCMinutes(minutes);
                newStart.setUTCSeconds(seconds);
                //new_start = new Date(new_start.toLocaleString('en-US', {timeZone: custom_timezone}))
                newStart = changeTimezone(newStart, customTimezone);
                const newItem = {
                    title: schedule.name,
                    start: newStart,
                    repeat_period: schedule.repeat_period,
                    type: "warning",
                    content: schedule.name,
                    scheduler_uuid: schedule.scheduler_uuid,
                };

                if (schedule.repeat_period === 0) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 1 ? "Weekday" :
                if (schedule.repeat_period === 1 && calendarDate.getUTCDay() % 6 !== 0) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 2 ? "Weekly" :  (every Monday)
                if (schedule.repeat_period === 2 && calendarDate.getUTCDay() === 1) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 3 ? "Monthly" :
                if (schedule.repeat_period === 3 && calendarDate.getUTCDate() === 1) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 4 ? "Send Once" :
                if (schedule.repeat_period === 4) {
                    let today = calendarDate;
                    let eventToday = startDate;
                    today.setHours(0, 0, 0, 0);
                    eventToday.setHours(0, 0, 0, 0);
                    if (today.getTime() === eventToday.getTime()) {
                        filteredCalendar.push(newItem);
                    }
                }
                // schedule.repeat_period === 5 ? "Quarterly" :
                if (
                    schedule.repeat_period === 5 &&
                    calendarDate.getUTCDate() === 1 &&
                    [0, 3, 6, 9].includes(calendarDate.getMonth())
                ) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 6 ? "Biannually" :
                if (
                    schedule.repeat_period === 6 &&
                    calendarDate.getUTCDate() === 1 &&
                    [0, 6].includes(calendarDate.getUTCMonth())
                ) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 7 ? "Annually" :
                if (
                    schedule.repeat_period === 7 &&
                    calendarDate.getUTCDate() === 1 &&
                    [0].includes(calendarDate.getUTCMonth())
                ) {
                    filteredCalendar.push(newItem);
                }
                // schedule.repeat_period === 8 ? "Weekends (Sat/Sun)" : ""
                if (schedule.repeat_period === 8 && calendarDate.getUTCDay() % 6 === 0) {
                    filteredCalendar.push(newItem);
                }
            }
        }
    }
    return filteredCalendar;
}
